import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ISSUE_META = [
  {
    client: "Formula 1",
    logo: "/clients/f1-logo.png",
    video: "/showcase/f1-video.mp4",
    accent: "#E10600",
    tilt: -5,
    logoClass: "max-h-24 md:max-h-32",
  },
  {
    client: "FIFA World Cup",
    logo: "/clients/fifa-logo.png?v=2",
    video: "/showcase/fifa-video.mp4",
    accent: "#6E2C9A",
    tilt: 4,
    logoClass: "max-h-28 md:max-h-36",
  },
  {
    client: "The Red Bulletin",
    logo: "/clients/redbulletin-logo.png",
    video: "/showcase/redbulletin-video.mp4",
    accent: "#00A6E6",
    tilt: -3,
    logoClass: "max-h-16 md:max-h-20",
  },
  {
    client: "Simply Abu Dhabi",
    logo: "/clients/robb-logo.png",
    video: "/showcase/simplyabudhabi-video.mp4",
    accent: "#C6A76B",
    tilt: 5,
    logoClass: "max-h-20 md:max-h-24",
  },
];

const MORE_CLIENTS = [
  {
    client: "Liverpool FC",
    logo: "/clients/liverpool-logo.png",
    logoClass: "max-h-24 md:max-h-32",
  },
  {
    client: "Lufthansa",
    logo: "/clients/lufthansa-logo.png",
    logoClass: "max-h-10 md:max-h-14",
  },
  {
    client: "The Red Bulletin",
    logo: "/clients/redbulletin-logo.png",
    logoClass: "max-h-12 md:max-h-16",
  },
  {
    client: "Automobilist",
    logo: "/clients/automobilist-logo.png",
    logoClass: "max-h-20 md:max-h-24",
  },
];

const TOP_LOGOS = [
  {
    client: "Formula 1",
    logo: "/clients/f1-logo.png",
    logoClass: "max-h-24 md:max-h-32",
  },
  {
    client: "FIFA World Cup",
    logo: "/clients/fifa-logo.png?v=2",
    logoClass: "max-h-28 md:max-h-36",
  },
  {
    client: "Robb Report",
    logo: "/clients/robb-logo.png",
    logoClass: "max-h-24 md:max-h-32",
  },
  {
    client: "Newsweek",
    logo: "/clients/newsweek-logo.png",
    logoClass: "max-h-20 md:max-h-24",
  },
];

export default function Showcase() {
  const { t, lang } = useLanguage();
  return (
    <section id="showcase" data-testid="showcase-section" className="relative py-32 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.22em] uppercase text-[#8BC53F] mb-4">{t.showcase.kicker}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter text-balance">
              {lang === "ar" ? (
                <>من <span className="italic">أعمالنا</span> السابقة.</>
              ) : (
                <>Previously produced <span className="italic">for</span>.</>
              )}
            </h2>
          </div>
          <p className="md:col-span-5 text-zinc-400 leading-relaxed">
            {t.showcase.sub}
          </p>
        </div>

        {/* Partner logo roster strip */}
        <div
          data-testid="client-strip"
          className="mb-16 rounded-3xl bg-white/95 backdrop-blur border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] px-6 md:px-12 py-10"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 items-center">
            {TOP_LOGOS.map((it) => (
              <motion.div
                key={`logo-${it.client}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                data-testid={`client-logo-${it.client.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-center h-32 md:h-40"
              >
                <img
                  src={it.logo}
                  alt={it.client}
                  className={`w-auto object-contain ${it.logoClass}`}
                />
              </motion.div>
            ))}
          </div>

          <div className="my-8 md:my-10 flex items-center gap-4">
            <span className="h-px flex-1 bg-zinc-300" />
            <span className="text-[10px] tracking-[0.28em] uppercase text-zinc-500">
              {t.showcase.moreHighlights}
            </span>
            <span className="h-px flex-1 bg-zinc-300" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 items-center">
            {MORE_CLIENTS.map((it) => (
              <motion.div
                key={`logo-${it.client}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                data-testid={`client-logo-${it.client.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-center h-24 md:h-32"
              >
                <img
                  src={it.logo}
                  alt={it.client}
                  className={`w-auto object-contain ${it.logoClass}`}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Magazine demo grid — iPhone video demos on a white card matching the roster */}
        <div className="rounded-3xl bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] p-6 md:p-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {ISSUE_META.map((it, i) => (
              <motion.figure
                key={it.client}
                initial={{ y: 40, opacity: 0, rotate: it.tilt / 2 }}
                whileInView={{ y: 0, opacity: 1, rotate: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                data-testid={`showcase-cover-${i}`}
                className="group relative"
              >
                <div className="relative overflow-hidden rounded-2xl aspect-[374/765] bg-white">
                  <video
                    src={`${it.video}?v=2`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label={`${it.client} demo`}
                    className="absolute inset-0 h-full w-full object-contain"
                  />

                  {/* Accent dot */}
                  <span
                    className="absolute top-3 right-3 h-2 w-2 rounded-full"
                    style={{ backgroundColor: it.accent, boxShadow: `0 0 16px ${it.accent}` }}
                  />
                </div>

                <figcaption className="mt-4 px-1">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1">
                    {it.client}
                  </p>
                  <p className="font-display text-base md:text-lg tracking-tight text-zinc-900 leading-tight">
                    {t.showcase.issues[i].title}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">{t.showcase.issues[i].meta}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>

        {/* Featured case study — FIFA World Cup Qatar 2022 · The Final Magazine */}
        <FeaturedCase />

        <p className="mt-12 text-center text-xs text-zinc-500 tracking-widest uppercase">
          {t.showcase.bottomNote}
        </p>
      </div>
    </section>
  );
}

function FeaturedCase() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { t, lang } = useLanguage();

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {});
  };

  return (
    <motion.section
      initial={{ y: 40, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8 }}
      data-testid="featured-case"
      className="mt-20 md:mt-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center"
    >
      {/* Left: description */}
      <div className="md:col-span-7 order-2 md:order-1">
        <p className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#F72210] mb-6">
          {t.showcase.featuredKicker}
        </p>
        <h3 className="font-display text-3xl md:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.05] text-balance">
          {lang === "ar" ? (
            <>كأس العالم FIFA<br />قطر 2022 الرسمية</>
          ) : (
            <>Official FIFA World Cup<br />Qatar 2022</>
          )}
        </h3>
        <p className="mt-6 font-display italic text-2xl md:text-3xl text-zinc-200">
          {t.showcase.featuredSub}
        </p>
        <p className="mt-2 text-sm tracking-[0.22em] uppercase text-zinc-500">
          {t.showcase.featuredMeta}
        </p>
        <p className="mt-8 max-w-xl text-zinc-300 leading-relaxed">
          {t.showcase.featuredBody}
        </p>
      </div>

      {/* Right: iPhone video, no white card, floats freely */}
      <div className="md:col-span-5 order-1 md:order-2 flex justify-center">
        <div
          data-testid="featured-video-frame"
          className="relative w-full max-w-[360px]"
          style={{ aspectRatio: "470 / 970" }}
        >
          <div className="absolute -inset-6 rounded-[40px] bg-[#6E2C9A]/30 blur-3xl pointer-events-none" />
          <video
            ref={videoRef}
            src="/showcase/fifa-final-featured.mp4?v=2"
            poster="/showcase/fifa-final-poster.jpg"
            autoPlay
            muted={muted}
            loop
            playsInline
            preload="metadata"
            aria-label="FIFA World Cup Qatar 2022 · The Final Magazine demo"
            className="relative h-full w-full object-contain"
          />
          <button
            type="button"
            onClick={toggleMute}
            data-testid="featured-video-mute-toggle"
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="absolute bottom-4 right-4 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:bg-black/80 transition-colors"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </div>
      </div>
    </motion.section>
  );
}
